(function () {
  'use strict';

  // ─── Config ───────────────────────────────────────────────────────────────
  const BAR_ID    = 'fsb-bar';
  const CURRENCY  = (Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
  const LOCALE    = document.documentElement.lang || 'en';

  // Threshold is injected by Liquid from the Theme Editor setting
  const THRESHOLD = Number(window.fsbThreshold);

  // Translations are injected by Liquid via the t filter
  const i18n = window.fsbTranslations || {
    title:    'Free Shipping',
    away:     'away',
    addMore:  'Add {amount} more to get free shipping.',
    unlocked: '🎉 You\'ve unlocked free shipping!',
    loading:  'Calculating...',
  };

  // ─── Guard ────────────────────────────────────────────────────────────────
  if (!THRESHOLD || THRESHOLD <= 0) {
    console.warn('[FSB] Threshold is not set or is 0. Set it in the Theme Editor.');
    return;
  }

  // ─── State ────────────────────────────────────────────────────────────────
  let lastCartTotal = null;
  let debounceTimer = null;
  let observer      = null;

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function formatCurrency(amount) {
    return new Intl.NumberFormat(LOCALE, {
      style:    'currency',
      currency: CURRENCY,
    }).format(amount);
  }

  function decodeHTML(html) {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  function t(key) {
    return decodeHTML(i18n[key] || key);
  }

  // ─── Cart Drawer Selectors ────────────────────────────────────────────────
  // Custom selector set by the merchant in Theme Editor takes priority.
  // Otherwise we try a list of common cart drawer containers.
  const CUSTOM_SELECTOR = (window.fsbCustomSelector || '').trim();

  const DEFAULT_SELECTORS = [
    '#CartDrawer .drawer__cart-items-wrapper',
    '#CartDrawer-Form',
    '#cart-drawer-items',
    '#CartDrawer .cart-drawer__inner',
    '[data-cart-drawer] .cart-items',
    '.cart-drawer__content',
    '#ajax-cart-inner',
    '.js-cart-drawer-content',
    '#cart-notification-product',
  ];

  function findDrawerTarget() {
    if (CUSTOM_SELECTOR) {
      const el = document.querySelector(CUSTOM_SELECTOR);
      if (el) return el;
      console.warn('[FSB] Custom selector not found:', CUSTOM_SELECTOR);
    }

    for (const sel of DEFAULT_SELECTORS) {
      const el = document.querySelector(sel);
      if (el) return el;
    }

    return null;
  }

  // ─── Create Bar HTML ──────────────────────────────────────────────────────
  function createBar() {
    const bar = document.createElement('div');
    bar.id = BAR_ID;
    bar.innerHTML = `
      <div style="
        padding: 12px 16px;
        background: var(--fsb-bar-background);
        border-radius: var(--fsb-border-radius);
        margin: 0 0 12px 0;
        font-family: inherit;
        box-sizing: border-box;
      ">
        <div style="
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
        ">
          <span style="font-size: 16px;">🚚</span>
          <span
            id="fsb-title"
            style="
              font-weight: 600;
              font-size: var(--fsb-font-size);
              flex: 1;
              color: inherit;
            "
          ></span>
          <span
            id="fsb-remaining"
            style="
              font-size: 12px;
              opacity: 0.65;
              color: inherit;
            "
          ></span>
        </div>

        <div style="
          width: 100%;
          height: 8px;
          background: var(--fsb-track-color);
          border-radius: 999px;
          overflow: hidden;
          margin-bottom: 8px;
          position: relative;
        ">
          <div
            id="fsb-fill"
            style="
              height: 100%;
              width: 0%;
              background: var(--fsb-bar-color);
              border-radius: 999px;
              transition: width 0.4s ease, background 0.3s ease;
              display: block;
            "
          ></div>
        </div>

        <p
          id="fsb-message"
          style="
            font-size: 12px;
            opacity: 0.75;
            margin: 0;
            color: inherit;
          "
        ></p>
      </div>
    `;
    return bar;
  }

  // ─── Render Bar ───────────────────────────────────────────────────────────
  function renderBar(cartTotal) {
    const fill      = document.getElementById('fsb-fill');
    const message   = document.getElementById('fsb-message');
    const remaining = document.getElementById('fsb-remaining');
    const title     = document.getElementById('fsb-title');

    if (!fill || !message || !remaining || !title) return;

    const total    = Number(cartTotal);
    const progress = Math.min((total / THRESHOLD) * 100, 100);
    const left     = Math.max(THRESHOLD - total, 0);
    const reached  = total >= THRESHOLD;

    title.textContent = t('title');

    requestAnimationFrame(() => {
      fill.style.width = `${progress}%`;
    });

    if (reached) {
      fill.style.background  = 'var(--fsb-bar-color-reached)';
      remaining.textContent  = '';
      message.textContent    = t('unlocked');
    } else {
      fill.style.background = 'var(--fsb-bar-color)';
      remaining.textContent = `${formatCurrency(left)} ${t('away')}`;
      message.textContent = t('addMore').replace('[amount]', formatCurrency(left));
    }

    console.log('[FSB] Rendered:', { cartTotal, THRESHOLD, progress, reached });
  }

  // ─── Inject Bar ───────────────────────────────────────────────────────────
  function injectBar() {
    if (document.getElementById(BAR_ID)) return true;

    const target = findDrawerTarget();
    if (!target) return false;

    const bar = createBar();
    target.insertBefore(bar, target.firstChild);
    return true;
  }

  // ─── Sync with Cart ───────────────────────────────────────────────────────
  async function syncBar() {
    try {
      const res  = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await res.json();
      const total = Number(cart.total_price) / 100;

      // Skip re-render if nothing changed and bar is already visible
      if (total === lastCartTotal && document.getElementById(BAR_ID)) return;
      lastCartTotal = total;

      // Pause observer to avoid render triggering another sync
      if (observer) observer.disconnect();

      if (!document.getElementById(BAR_ID)) {
        injectBar();
      }

      renderBar(total);

      // Reconnect observer
      const cartDrawer = document.querySelector('#CartDrawer');
      if (observer && cartDrawer) {
        observer.observe(cartDrawer, { childList: true, subtree: true });
      }
    } catch (err) {
      console.warn('[FSB] Failed to sync cart:', err);
    }
  }

  // ─── Debounce ─────────────────────────────────────────────────────────────
  function debouncedSync() {
    // Optimistic UI: If the theme wiped out our bar, immediately re-inject it 
    // with the last known total to prevent flickering while we fetch the new total.
    if (!document.getElementById(BAR_ID) && lastCartTotal !== null) {
      if (injectBar()) {
        renderBar(lastCartTotal);
      }
    }

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncBar, 250);
  }

  // ─── Watch Cart Drawer ────────────────────────────────────────────────────
  function watchCartDrawer() {
    const cartDrawer = document.querySelector('#CartDrawer');

    if (cartDrawer) {
      observer = new MutationObserver(debouncedSync);
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }

    // Listen to common cart update events from various themes
    document.addEventListener('cart:updated',  debouncedSync);
    document.addEventListener('cart-update',   debouncedSync);
    document.addEventListener('cart:change',   debouncedSync);

    // Fallback polling every 2s to catch themes that don't fire events
    setInterval(syncBar, 2000);
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────
  async function boot() {
    try {
      // Fetch initial cart total
      const res  = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await res.json();
      lastCartTotal = Number(cart.total_price) / 100;

      // Try to inject the bar — retry up to 20 times (themes load drawers lazily)
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;

        if (injectBar()) {
          renderBar(lastCartTotal);
          clearInterval(interval);
        }

        if (attempts > 20) {
          console.warn('[FSB] Could not find cart drawer after 20 attempts. Set a custom selector in the Theme Editor.');
          clearInterval(interval);
        }
      }, 300);

      watchCartDrawer();

      console.log('[FSB] Initialized. Threshold:', THRESHOLD, CURRENCY);
    } catch (err) {
      console.warn('[FSB] Boot failed:', err);
    }
  }

  boot();

})();
